import { useTranslation } from 'react-i18next';

import { Amount, Ltr } from '@/components/amount';
import { cn } from '@/lib/utils';

/**
 * The DSH-00 §14.1.8 body kinds that are "plain coloured blocks" in the
 * design — KPI cards, ranked horizontal bars, and the small tables. Real
 * charts (trends, donuts, the forecast) ride the ECharts <Chart> wrapper.
 */

/**
 * §14.1.9: change pill — sign, 1 decimal, arrow by direction, tone by goodness.
 *
 * BR-DSH-02-06 is this component's default case, stated the other way round:
 * "A change pill must be red with a downward arrow when the value fell and
 * green with an upward arrow when it rose." With `downIsGood` false — every
 * DSH-02 caller — `rose` decides the arrow AND `good` equals `rose`, so up is
 * green and down is red and the two can never come apart. BR-DSH-10-06 is the
 * same sentence for the report shell, with the boundary spelled out: "green
 * with an upward arrow when it is ZERO OR ABOVE", which is why `rose` tests
 * the minus sign rather than the number — `+0.0` is not negative.
 *
 * N-30 / EC-19-46 are the reason `rose` is read off the SIGN and nothing else,
 * and `good` is a separate question answered by `downIsGood`. The prototype
 * drew "Total Expenses +4.1%" with a DOWNWARD arrow, because it let the arrow
 * carry the good-news judgement — so the arrow and the number it sat beside
 * disagreed. Here the arrow can only ever agree with the sign, and the colour
 * carries the judgement on its own.
 *
 * THAT PROTOTYPE DEFECT HAS TWO IDENTIFIERS AND THIS IS WHERE BOTH ARE FIXED.
 * ERR-DSH-01-06 is the observation — "Total Expenses shows a positive percent
 * beside a downward arrow" — and BR-DSH-01-04 is the repair: "The arrow inside
 * a change pill must point up when the value rose and down when the value
 * fell, and the pill colour must be green when the change is good for the
 * business and red when it is bad. For expenses, a rise is bad, so the pill
 * must be red and the arrow must point up." The spec closes the loop itself,
 * in brackets: "[This rule fixes the rendered defect where Total Expenses
 * shows +4.1% beside a down arrow.]", and §14.10 lists it as known defect 1 of
 * the prototype, "recorded so a build fixes them rather than copying them".
 *
 * SO ERR-DSH-01-06 IS NOT A DESCRIPTION OF THIS BUILD, and a reader who takes
 * it for one will "restore" the defect. It is named here, at the code that
 * makes it impossible, rather than left unnamed for someone to rediscover from
 * the §14 table and implement in good faith. The structure is what forbids it:
 * `rose` is a test on the minus sign of the printed string and reads nothing
 * else, so no value of `downIsGood` and no data can put a down glyph beside a
 * positive percent. `widgets-change-pill.test.tsx` asserts that ABSENCE
 * directly, in both directions of `downIsGood`, because "the up arrow is
 * present" would still pass on a component that drew both.
 *
 * BR-DSH-01-04's second half is the caller's, and DSH-01 passes
 * `downIsGood: true` for the expenses card alone — which is what makes the
 * pill red while the arrow points up, the exact pairing the rule spells out.
 *
 * N-31 — "the period being compared against must be named in words beside the
 * figure" — is the CALLER's half, and every caller does it: the two dashboards
 * render `dash.vsLastQuarter` / `dash.vsPrevious` next to the pill and the
 * reports shell renders `reports.vsPrevious`. A pill with no such label would
 * leave the reader guessing which window it means.
 */
export function ChangePill({ changePct, downIsGood = false }: { changePct: string | null; downIsGood?: boolean }) {
  const { t } = useTranslation();
  // A null change draws NO pill — the F-82 / N-29 "no comparison" shape, as far
  // as the screen can take it. Whether the SERVER should send null when the
  // previous period is ZERO is not settled and this build does not pretend it
  // is: N-29 says show "no comparison", BR-DSH-10-05 says show +100.0%, and
  // PART 14 §14.9 question 33 records the two as unreconciled. `reportPct`
  // follows BR-DSH-10-05 today, so the pill is drawn.
  if (changePct === null) return null;
  const rose = !changePct.startsWith('-');
  const good = rose !== downIsGood;
  return (
    <bdi
      dir="ltr"
      className={cn(
        'inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-xs font-medium',
        good ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700',
      )}
    >
      {/* NFR-01: the glyphs and the sign are dictionary text (unit.up/down/
          percent) — which glyph means "rose" is the translation's decision. */}
      {rose ? t('unit.up') : t('unit.down')} {t('unit.percent', { value: changePct })}
    </bdi>
  );
}

export function KpiValue({
  value,
  currency,
  tone,
}: {
  value: string;
  currency?: string | undefined;
  tone?: 'pos' | 'neg' | 'warn' | undefined;
}) {
  return (
    <div
      className={cn(
        'text-2xl font-semibold',
        tone === 'pos' && 'text-emerald-700 dark:text-emerald-400',
        tone === 'neg' && 'text-red-600 dark:text-red-400',
        tone === 'warn' && 'text-amber-700 dark:text-amber-400',
      )}
    >
      {currency === undefined ? <Ltr value={value} className="font-mono tabular-nums" /> : <Amount value={value} currency={currency} />}
    </div>
  );
}

/**
 * Ranked horizontal bars (§5j): name, value, proportional track. Top rows
 * only. F-84 — `bar_pct = max(2, value ÷ largest × 100)`: the 2% floor is
 * the rule's own ("without it, a tiny value would draw no bar at all and
 * look like missing data"); §5j's share-of-largest is the same formula
 * without restating the floor.
 */
/**
 * `currency` defaults to the dollar only because every board that does not
 * pass one reports in it; DSH-04 passes what the SERVER says its figures are
 * stated in (BR-DSH-04-10), because a bar labelled USD beside a shekel figure
 * is the same defect as a total that added the two.
 */
export function RankedBars({
  rows,
  tone = 'primary',
  currency = 'USD',
}: {
  rows: { name: string; value: string }[];
  tone?: 'primary' | 'pos' | 'muted';
  currency?: string;
}) {
  const { t } = useTranslation();
  if (rows.length === 0) return <EmptyLine text={t('dash.noData')} />;
  const max = Math.max(...rows.map((r) => Math.abs(Number(r.value))), 1);
  return (
    <div className="flex flex-col gap-2">
      {rows.map((row) => (
        <div key={row.name}>
          <div className="flex items-baseline justify-between gap-2 text-sm">
            <span className="truncate">{row.name}</span>
            <Amount value={row.value} currency={currency} className="text-sm" />
          </div>
          <div className="mt-0.5 h-[9px] rounded-full bg-muted">
            <div
              className={cn(
                'h-full rounded-full',
                tone === 'pos' ? 'bg-emerald-500' : tone === 'muted' ? 'bg-muted-foreground/40' : 'bg-primary',
              )}
              style={{ width: `${Math.max(2, (Number(row.value) / max) * 100)}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * BR-DSH-00-20: a widget's own empty message, never zero height — which is
 * EC-19-50's rule for the whole product: no screen renders an empty region;
 * every state shows content or a line saying why there is none. GEN-156 and
 * GEN-157 are the same rule's PART 4 numbers: a written empty state for
 * every list/table/chart, offering the one action that would end the
 * emptiness where the caller passes one (the po.empty + New pattern).
 *
 * EC-19-01 is the same rule for every list in the product: "every one must
 * show a short line of text saying it is empty, worded for that particular
 * list… a blank white card must never be shown." The `text` is a parameter and not a default precisely because the
 * rule says WORDED FOR THAT LIST: an empty alert list is GOOD news and has to
 * say so ("Nothing is below its reorder level"), which a shared sentence
 * cannot do. It is also the shape ERR-DSH-02-04 / ERR-DSH-03-03 /
 * ERR-DSH-04-03 all require — a filter that matches nothing empties each
 * widget in its own words, and there is deliberately no page-wide banner.
 *
 * ERR-DSH-02-01 is the same machinery answering a different question — "opened
 * the screen on a brand-new company: every widget shows its own empty message;
 * NO ERROR APPEARS." A company with no sales is not a fault, so nothing on the
 * board may say it is: each KPI reads its own zero (DSH-02 §15: "`Open
 * Quotations` of `0` renders as `0` with a caption of `$0.00`, not as a blank
 * card"), each chart and table draws this line, and the page draws no banner
 * of any kind. That last part is why the rule belongs to the empty state and
 * not to an error path — the absence of the error IS the requirement.
 *
 * ERR-DSH-03-01 is DSH-03's word-for-word twin of that rule — "Opened the
 * screen with no items set up → Every widget shows its own empty message" —
 * and DSH-03 §15 spells out the same two halves: "a company with no items
 * shows zeros and per-widget empty messages, NEVER A BROKEN CARD." The KPI
 * half is the three `KpiValue`s reading their own `0`; the chart and table
 * half is this line, reached by every one of DSH-03's eight non-KPI widgets
 * (the five tables test `.length === 0` themselves, the two ranked charts
 * reach `RankedBars`' own guard above, and the movement trend gets an empty
 * `buckets` array because a company with no items has no stock movements to
 * bucket). It is grouped with ERR-DSH-02-01 and ERR-DSH-03-03 rather than
 * given its own site because all three are one behaviour asked for by three
 * different causes — no data yet, no data matching the filter, no data at
 * all — and splitting them would imply the screen distinguishes cases it
 * deliberately does not.
 */
export function EmptyLine({ text }: { text: string }) {
  return <p className="py-6 text-center text-sm text-muted-foreground">{text}</p>;
}

/**
 * BR-DSH-02-15 / DSH-04 §5l: the days-overdue colour ramp (F-14's grey/amber
 * /red thresholds).
 *
 * EC-19-75 IS NOW RULED, and this note is kept rather than deleted because it
 * was one of the two places carrying the open question. Aged debt was BANDED
 * two ways in the source material — the dashboard's four buckets against the
 * statement's five. DECISION 5 (26 Aug 2026): five everywhere, the statement's
 * bands winning, "because 61–90 is a distinction an accountant chases on".
 * The statement's aging is built and the financial dashboard's `arAging` panel
 * is widened to the same five (`financial-dashboard-page.tsx`).
 *
 * THIS RAMP IS NOT THAT BANDING, AND THE DISTINCTION SURVIVES THE RULING. It
 * colours a single row's DAYS-OVERDUE NUMBER on two tables — it is not a
 * bucket, sums nothing, and BR-DSH-02-15 states its own thresholds (grey to
 * 30, amber 31–60, red above 60) in a sentence D5 does not touch. The two
 * happen to agree where it matters: everything the panel paints red, this
 * paints red.
 */
export function overdueTone(days: number): string {
  if (days > 60) return 'text-red-600 dark:text-red-400';
  if (days > 30) return 'text-amber-700 dark:text-amber-400';
  return 'text-muted-foreground';
}

/**
 * Compact widget table chrome shared by the §5k-style tables.
 *
 * The scroll container is the one `Table` gives every other table in the
 * product (§4.3.4: wide content scrolls inside its container, the page never
 * scrolls sideways). It became load-bearing when these tables started using
 * `<TableCell>` for BR-ADM-08-08 — a shared cell does not wrap, so a long
 * party name that used to reflow inside a widget card now scrolls instead of
 * spilling out of it.
 */
export function WidgetTable({ head, children }: { head: string[]; children: React.ReactNode }) {
  return (
    <div className="w-full overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr>
            {head.map((h, i) => (
              <th
                key={h + String(i)}
                className={cn(
                  'pb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground',
                  i === 0 ? 'text-start' : 'text-end',
                )}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}
